import React, { useRef, useEffect, useState, useCallback } from "react";
import SearchBox from "../common/SearchBox";
import RenderOnScrollEntry from "../common/RenderOnScrollentry";
import { useMenu } from "../layout/MenuContext";

const DesktopView = ({
  items,
  selectedItem,
  onSearch,
  searchPlaceholder,
  contextMenuItems,
  onItemClick,
  onSave,
  onDelete,
  onReset,
  onClose,
  Card,
  Editor,
  addNewItem,
  eventName,
  fallback,
  hideOnSelect = false,
  cardExtraProps = {},
}) => {
  const dashboardRef = useRef(null);
  const { showMenu } = useMenu();
  const [containerHeight, setContainerHeight] = useState("100vh");
  const [searchTerm, setSearchTerm] = useState("");
  const [isRegexMode, setIsRegexMode] = useState(() => {
    return localStorage.getItem("search-regex-mode") === "true";
  });

  useEffect(() => {
    const updateHeight = () => {
      if (dashboardRef.current) {
        const topOffset = dashboardRef.current.getBoundingClientRect().top;
        setContainerHeight(`calc(100vh - ${topOffset}px)`);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const handleContextMenu = (e) => {
    if (
      e.target.classList.contains("dashboard-container") ||
      e.target.classList.contains("dashboard-grid")
    ) {
      e.preventDefault();
      const menu = [
        ...contextMenuItems,
        { label: "Add New", onClick: addNewItem },
      ];
      const sortedMenu = menu.sort((a, b) => a.label.localeCompare(b.label));
      showMenu(sortedMenu, e.clientX, e.clientY);
    }
  };

  const handleSearch = useCallback(
    (term, regexMode) => {
      setSearchTerm(term);
      setIsRegexMode(regexMode);
      onSearch(term);
    },
    [onSearch]
  );

  const showCreatedItem = useCallback(
    (e) => {
      handleSearch("", isRegexMode);
      if (e.detail) {
        onItemClick(e.detail);
      }
    },
    [onItemClick, isRegexMode, handleSearch]
  );

  useEffect(() => {
    window.addEventListener(eventName, showCreatedItem);
    return () => {
      window.removeEventListener(eventName, showCreatedItem);
    };
  }, [eventName, showCreatedItem]);

  const filteredItems = items.filter((item) =>
    Object.values(item).some((value) => {
      const val = (
        (typeof value === "object" ? JSON.stringify(value) : value) ?? ""
      ).toString();
      if (isRegexMode) {
        try {
          const regex = new RegExp(searchTerm, "i");
          return regex.test(val);
        } catch (e) {
          // If the regex is invalid, fall back to normal search
          return val.toLowerCase().includes(searchTerm.toLowerCase());
        }
      } else {
        return val.toLowerCase().includes(searchTerm.toLowerCase());
      }
    })
  );

  const renderCard = (item) => {
    if (Card) {
      return (
        <div key={item.key || item.id}>
          <RenderOnScrollEntry
            children={
              <Card
                id={item.id}
                key={item.key || item.id}
                item={item}
                onClick={() => onItemClick(item)}
                {...cardExtraProps}
              />
            }
            fallback={fallback || <div>Loading...</div>}
          />
        </div>
      );
    }
    // Default card rendering if Card component is not provided
    return (
      <div
        key={item.id}
        onClick={() => onItemClick(item)}
        className="card-glass cursor-pointer"
      >
        <h3>{item.name || "Unnamed Item"}</h3>
        <p>{item.description || "No description"}</p>
      </div>
    );
  };

  return (
    <div
      onContextMenu={handleContextMenu}
      ref={dashboardRef}
      className="dashboard-container w-full dark-slim-scrollbar"
      style={{ height: containerHeight }}
    >
      <div className="container mx-auto p-4">
        {(!selectedItem || !hideOnSelect) && (
          <>
            <div className="absolute top-4 left-[50%] translate-x-[-50%] w-[400px]">
              <SearchBox
                value={searchTerm}
                onSearch={handleSearch}
                placeholder={searchPlaceholder}
              />
            </div>
            <div className="dashboard-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-10">
              {filteredItems.map(renderCard)}
            </div>
          </>
        )}
        {selectedItem && Editor && (
          <Editor
            item={selectedItem}
            onSave={onSave}
            onClose={onClose}
            onDelete={onDelete ? () => onDelete(selectedItem.id) : undefined}
            onReset={() => onReset && onReset(selectedItem.id)}
          />
        )}
      </div>
    </div>
  );
};

export default DesktopView;
